"use client";

import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, BookOpen, Users, Database, Activity } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const nodes = [{
  id: "learners",
  label: "Little Explorers",
  icon: Users,
  pos: "top-[15%] left-1/2 -translate-x-1/2 -translate-y-1/2",
  color: "bg-chart-1 text-primary-foreground",
  delay: "delay-100"
}, {
  id: "teachers",
  label: "Magical Teachers",
  icon: BookOpen,
  pos: "top-[80%] left-[85%] -translate-x-1/2 -translate-y-1/2",
  color: "bg-chart-2 text-primary-foreground",
  delay: "delay-200"
}, {
  id: "parents",
  label: "Happy Parents",
  icon: ShieldCheck,
  pos: "top-[80%] left-[15%] -translate-x-1/2 -translate-y-1/2",
  color: "bg-chart-3 text-primary-foreground",
  delay: "delay-300"
}];
const connections = [{
  id: "conn-learners",
  x1: "50%",
  y1: "50%",
  x2: "50%",
  y2: "15%",
  midTop: "32.5%",
  midLeft: "50%",
  tooltip: "Speaking with Confidence"
}, {
  id: "conn-teachers",
  x1: "50%",
  y1: "50%",
  x2: "85%",
  y2: "80%",
  midTop: "65%",
  midLeft: "67.5%",
  tooltip: "Fun Games & Songs"
}, {
  id: "conn-parents",
  x1: "50%",
  y1: "50%",
  x2: "15%",
  y2: "80%",
  midTop: "65%",
  midLeft: "32.5%",
  tooltip: "Tracking Magical Progress"
}];
export default function TheEduStreamEcosystem() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="95387" id="our-global-community" className="relative py-24 overflow-hidden bg-background" ref={ref}>
      {}
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{
      backgroundImage: "radial-gradient(hsl(var(--primary) / 0.15) 1px, transparent 1px)",
      backgroundSize: "32px 32px"
    }} />

      {}
      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes dash-flow {
          to {
            stroke-dashoffset: -24;
          }
        }
        .animate-dash-flow {
          animation: dash-flow 1.5s linear infinite;
        }
      `
    }} />

      <div className="container mx-auto px-4 relative z-10">
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-foreground mb-4">
            Our Global Community
          </h2>
          <p className="text-xl text-muted-foreground font-sans">
            Join over 1,000+ happy learners discovering the joy of Spanish! See how we 
            connect children, teachers, and families in one magical learning world.
          </p>
        </div>

        <div className={`relative w-full max-w-4xl mx-auto min-h-[500px] md:min-h-[600px] transition-all duration-1000 delay-300 transform ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
          {}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
            {connections.map((conn, i) => <g key={conn.id} data-index={i}>
                {}
                <line x1={conn.x1} y1={conn.y1} x2={conn.x2} y2={conn.y2} className="stroke-secondary/30" strokeWidth="3" />
                {}
                <line x1={conn.x1} y1={conn.y1} x2={conn.x2} y2={conn.y2} className="stroke-primary/50 animate-dash-flow" strokeWidth="4" strokeDasharray="8 16" strokeLinecap="round" />
              </g>)}
          </svg>

          {}
          <TooltipProvider delayDuration={100}>
            {connections.map((conn, i) => <div key={`trigger-${conn.id}`} data-index={i} className="absolute -translate-x-1/2 -translate-y-1/2 z-20" style={{
            top: conn.midTop,
            left: conn.midLeft
          }}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="group relative flex items-center justify-center size-10 rounded-full bg-card border-2 border-border shadow-md hover:border-primary hover:shadow-lg transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" aria-label={`View adventure detail: ${conn.tooltip}`}>
                      <Activity className="size-5 text-primary group-hover:text-primary transition-colors" />
                      <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="font-sans font-bold bg-primary text-primary-foreground rounded-xl shadow-xl border-none">
                    <p>{conn.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </div>)}
          </TooltipProvider>

          {}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 flex flex-col items-center">
            <div className="relative flex items-center justify-center size-28 md:size-36 rounded-[2.5rem] bg-primary text-primary-foreground shadow-2xl shadow-primary/30 border-4 border-card">
              <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />
              <Database className="size-12 md:size-16" />
              {}
              <div className="absolute inset-0 rounded-[2.5rem] border-2 border-primary animate-ping opacity-30" style={{
              animationDuration: '3.5s'
            }} />
            </div>
            <div className="mt-4 px-6 py-2 bg-card/90 backdrop-blur-md border-2 border-primary/20 rounded-full shadow-lg">
              <span className="text-base font-serif font-bold text-foreground whitespace-nowrap">
                LinguaKids World
              </span>
            </div>
          </div>

          {}
          {nodes.map((node, i) => <div key={node.id} data-index={i} className={`absolute z-20 flex flex-col items-center transition-all duration-700 transform ${node.pos} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} ${node.delay}`}>
              <div className={`flex items-center justify-center size-20 md:size-24 rounded-[2rem] shadow-xl border-4 border-card ${node.color} hover:scale-110 transition-transform duration-300 cursor-pointer`}>
                <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-white/30 to-transparent pointer-events-none" />
                <node.icon className="size-9 md:size-11" />
              </div>
              <div className="mt-3 px-4 py-1.5 bg-card/95 backdrop-blur-sm border-2 border-border rounded-full shadow-md">
                <span className="text-sm md:text-base font-bold text-foreground font-sans whitespace-nowrap">
                  {node.label}
                </span>
              </div>
            </div>)}
        </div>

        {}
        <div className={`mt-12 text-center transition-all duration-1000 delay-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="inline-flex items-center gap-2 px-8 py-3 bg-secondary/10 rounded-full border-2 border-secondary/20">
            <span className="text-2xl font-serif font-bold text-secondary">1,000+</span>
            <span className="text-lg font-sans font-semibold text-foreground">Happy Learners Growing Every Day!</span>
          </div>
        </div>
      </div>
    </section>;
}